'use client';

import { useState, useEffect } from 'react';
import { marketPulseSubscription, marketInsightsSubscription } from './market-data-subscriptions';

export interface EcosystemContext {
  tvl: number;
  tvlChange24h: number;
  dexVolume24h: number;
  protocolCount: number;
  stablecoinMcap: number;
  fees24h: number;
  topProtocols: Array<{
    name: string;
    slug: string;
    tvl: number;
  }>;
  dataTimestamp: string;
}

export interface GlobalMarketData {
  totalMarketCap: number;
  totalVolume24h: number;
  btcDominance: number;
  ethDominance: number;
  marketCapChange24h: number;
}

export interface GlobalContext {
  btc?: {
    price: number;
    change24h: number;
  } | null;
  eth?: {
    price: number;
    change24h: number;
  } | null;
  fearGreed?: {
    value: number;
    classification: string;
    timestamp: string;
  } | null;
  globalMarket?: GlobalMarketData | null;
}

export interface AbstractFearGreedComponents {
  globalMarketGravity?: number;
  priceMomentum: number;
  tvlHealth: number;
  volumeActivity: number;
  volatility: number;
  stablecoinFlows: number;
  protocolHealth: number;
  socialSentiment: number;
  // Legacy v1 fields (for backwards compatibility)
  volumeTrend?: number;
  protocolActivity?: number;
}

export interface AbstractFearGreed {
  value: number;
  classification: 'Extreme Fear' | 'Fear' | 'Neutral' | 'Greed' | 'Extreme Greed';
  components: AbstractFearGreedComponents;
  weights?: {
    globalMarketGravity?: number;
    priceMomentum: number;
    tvlHealth: number;
    volumeActivity: number;
    volatility: number;
    stablecoinFlows: number;
    protocolHealth: number;
    socialSentiment: number;
    // Legacy
    volumeTrend?: number;
    protocolActivity?: number;
  };
  version?: number;
  timestamp: string;
  dataQuality?: {
    componentsWithData: number;
    totalComponents: number;
    reliability: 'high' | 'medium' | 'low';
  };
  scoreHistory?: Array<{ value: number; timestamp: string }>;
  trend?: {
    direction: 'improving' | 'deteriorating' | 'stable' | 'new';
    label: string;
    delta: number;
  };
  // V4 ("Anchor + Stress Spread") additive audit envelope. All optional + additive — existing
  // consumers (v3 payloads) read undefined and are unaffected. Spec:
  // docs/superpowers/specs/2026-06-07-abstract-fear-greed-v4-methodology.md
  /** Broad-market gravity center (0-100). Equals components.globalMarketGravity. */
  anchor?: number;
  /** How the anchor was derived: global F&G, synthetic from majors, or none (forces low reliability). */
  anchorSource?: 'global' | 'synthetic' | 'none';
  /** Capped ETH/BTC beta residual folded into the anchor (signed points). */
  betaResidual?: number;
  /** Signed, evidence-bounded chain-idiosyncratic spread added to the anchor (negative = more stressed). */
  spread?: number;
  /** Coverage of the signal pool actually observed, 0-1 (scales the spread's max magnitude). */
  spreadConfidence?: number;
  /** Human-readable spread direction for the audit display. */
  spreadDirection?: 'more stressed than market' | 'calmer than market' | 'in line with market';
  /**
   * True-influence audit view: each present signal's ACTUAL signed point-contribution to the final
   * value (= SPREAD_GAIN * w_i * conf_i * s_i, rounded to 2 dp). Distinct from `components` (the
   * standard per-sub-index 0-100 bars). Keys appear only for present signals.
   */
  contributions?: {
    breadth?: number;
    tvl?: number;
    volume?: number;
    stablecoin?: number;
    economics?: number;
    volatility?: number;
    social?: number;
  };
  /** Per-signal signed contributions + presence flags (powers the methodology transparency view). */
  subSignals?: {
    breadth?: { s: number; present: boolean };
    tvl?: { s: number; present: boolean };
    volume?: { s: number; present: boolean };
    stablecoin?: { s: number; present: boolean };
    economics?: { s: number; present: boolean };
    volatility?: { s: number; present: boolean };
    social?: { s: number; present: boolean };
  };
}

export interface MarketPulse {
  summary: string;
  timestamp: string;
  generatedBy: string;
  metrics: {
    totalTokens: number;
    gainersCount: number;
    losersCount: number;
    neutralCount: number;
    topGainer: {
      symbol: string;
      change: number;
    } | null;
    topLoser: {
      symbol: string;
      change: number;
    } | null;
    highestVolume: {
      symbol: string;
      volume: number;
    } | null;
    averageChange: number;
    totalVolume: number;
    volatilityLevel: string;
    marketMomentum?: string;
  };
  ecosystemContext?: EcosystemContext | null;
  globalContext?: GlobalContext | null;
  abstractFearGreed?: AbstractFearGreed | null;
}

export interface TokenSignals {
  momentum: 'bullish' | 'neutral' | 'bearish';
  volume: 'above_average' | 'normal' | 'below_average';
  volatility: 'high' | 'moderate' | 'low';
  relativeStrength: number;
}

export interface EcosystemComparison {
  ecosystemTvlChange: number;
  tokenVsEcosystem: 'outperforming' | 'underperforming' | 'matching' | null;
}

export interface TokenInsightGlobalContext {
  btcChange24h?: number;
  btcCorrelation?: 'correlated' | 'diverging' | 'independent';
  fearGreedValue?: number;
  fearGreedClassification?: string;
}

export interface TokenInsight {
  analysis: string;
  signals: TokenSignals;
  timestamp: string;
  generatedBy: string;
  metrics: {
    price: number;
    change1h?: number;
    change6h?: number;
    change24h: number;
    volume24h: number;
    marketCap?: number;
    relativePerformance: number;
    trendAcceleration?: string;
  };
  ecosystemComparison?: EcosystemComparison | null;
  globalContext?: TokenInsightGlobalContext | null;
  isAbstractNative?: boolean;
  /**
   * The entity behind the asset (stamped by the backend insight pipeline when a
   * tracked handle/tag matches) — `entitySlug` is the dossier door.
   */
  socialContext?: {
    entityName?: string;
    entitySlug?: string;
    handleId?: string;
    sentiment?: string;
    username?: string;
  } | null;
}

export interface TokenInsights {
  [symbol: string]: TokenInsight;
}

interface UseMarketAnalysisReturn {
  pulse: MarketPulse | null;
  insights: TokenInsights | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

/**
 * Hook to fetch AI-generated market analysis from Firestore
 * Listens for real-time updates
 */
export function useMarketAnalysis(): UseMarketAnalysisReturn {
  const [pulse, setPulse] = useState<MarketPulse | null>(null);
  const [insights, setInsights] = useState<TokenInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    // Skip during build/SSR — the shared poller fetches a browser-only route.
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    setError(null);

    // Attach to the ONE shared market-snapshot poller for the pulse + insights slices.
    // Previously every mount of this hook opened two of its own Firestore onSnapshot
    // listeners; they now share a single reference-counted 60s poll of the cached
    // /api/market/snapshot route (see market-data-subscriptions).
    const pulseUnsubscribe = marketPulseSubscription.subscribe(
      (data) => {
        setPulse(data);
        setLastUpdated(new Date());
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.warn('Market pulse listener error:', err);
        setError('Failed to fetch market pulse');
        setLoading(false);
      },
    );

    const insightsUnsubscribe = marketInsightsSubscription.subscribe(
      (data) => {
        setInsights(data);
        setLastUpdated(new Date());
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.warn('Token insights listener error:', err);
        setError('Failed to fetch token insights');
        setLoading(false);
      },
    );

    return () => {
      pulseUnsubscribe();
      insightsUnsubscribe();
    };
  }, []);

  return {
    pulse,
    insights,
    loading,
    error,
    lastUpdated
  };
}

/**
 * Helper function to get insight for a specific token
 */
export function getTokenInsight(insights: TokenInsights | null, symbol: string): TokenInsight | null {
  if (!insights) return null;
  return insights[symbol.toLowerCase()] || null;
}

/**
 * Helper function to format time ago
 */
export function getTimeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}

/**
 * Helper function to check if data is stale (older than 12 hours)
 */
export function isDataStale(timestamp: string, maxAgeHours: number = 12): boolean {
  const now = new Date();
  const then = new Date(timestamp);
  const diffHours = (now.getTime() - then.getTime()) / (1000 * 60 * 60);
  return diffHours > maxAgeHours;
}

